/**
 * useSquarePaymentData
 *
 * Square決済DB (DS1: search_by_contact.php) をメインデータソースとして
 * 支払い状況を取得・キャッシュするフック。
 *
 * - invoices + payments + orders + refunds を一括取得
 * - detail.payment === 1 (確認済みフラグ) の予約はスキップ
 * - Pusher payment.updated イベントで該当 contact_code を自動リフレッシュ
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { SYS_API_BASE } from '../constants/calendar';
import { pusherService } from '../services/pusherService';
import type { SquarePaymentEntry } from '../utils/paymentStatus';

export type SquarePaymentCache = Record<string, SquarePaymentEntry>;

/** 1つの contact_code に対して DS1 を呼び出し、キャッシュエントリを返す */
async function fetchSingleContact(contactCode: string): Promise<SquarePaymentEntry> {
  const res = await fetch(
    `${SYS_API_BASE}/payment/search_by_contact.php?contact_code=${encodeURIComponent(contactCode)}`,
  );
  const json = await res.json();
  return {
    invoices: json.invoices ?? [],
    payments: json.payments ?? [],
    orders: json.orders ?? [],
    refunds: json.refunds ?? [],
    fetchedAt: Date.now(),
  };
}

/** 複数の contact_code を並列バッチで取得 (concurrency=5) */
async function fetchBatch(
  contactCodes: string[],
): Promise<Record<string, SquarePaymentEntry>> {
  const CONCURRENCY = 5;
  const result: Record<string, SquarePaymentEntry> = {};
  const queue = [...contactCodes];

  while (queue.length > 0) {
    const batch = queue.splice(0, CONCURRENCY);
    const settled = await Promise.allSettled(
      batch.map(async (code) => {
        const entry = await fetchSingleContact(code);
        return { code, entry };
      }),
    );
    for (const s of settled) {
      if (s.status === 'fulfilled') {
        result[s.value.code] = s.value.entry;
      }
    }
  }

  return result;
}

export function useSquarePaymentData(targetStatuses: number[] = [1, 2, 3, 4, 6]) {
  const [squarePaymentCache, setSquarePaymentCache] = useState<SquarePaymentCache>({});
  const [squarePaymentLoading, setSquarePaymentLoading] = useState(false);
  const cacheRef = useRef(squarePaymentCache);
  cacheRef.current = squarePaymentCache;

  /**
   * 予約リストを受け取り、対象ステータスかつ未確認の予約の決済データを一括取得
   */
  const fetchPaymentData = useCallback(
    async (reservationsList: any[]) => {
      if (!Array.isArray(reservationsList) || reservationsList.length === 0) return;

      const contactCodes = reservationsList
        .filter((r: any) => {
          // 対象ステータスのみ
          if (!targetStatuses.includes(Number(r.status))) return false;
          // 確認済みフラグがある予約はスキップ
          const isManualPaid =
            Number(r.detail?.payment) === 1 || r.detail?.payment === true;
          if (isManualPaid) return false;
          return true;
        })
        .map((r: any) => r.contact_code || r.detail?.contact_id)
        .filter(Boolean);

      // 重複排除 + キャッシュ済みを除外
      const unique = [...new Set(contactCodes)].filter(
        (code) => !cacheRef.current[code],
      );

      if (unique.length === 0) return;

      setSquarePaymentLoading(true);
      try {
        const result = await fetchBatch(unique);
        setSquarePaymentCache((prev) => ({ ...prev, ...result }));
      } catch (err) {
        console.error('[useSquarePaymentData] Failed to fetch:', err);
      } finally {
        setSquarePaymentLoading(false);
      }
    },
    [targetStatuses],
  );

  /**
   * キャッシュからエントリを取得
   */
  const getPaymentData = useCallback(
    (contactCode: string): SquarePaymentEntry | undefined => {
      return squarePaymentCache[contactCode];
    },
    [squarePaymentCache],
  );

  /**
   * 単一の contact_code を再フェッチしてキャッシュ更新
   */
  const refreshSingleContact = useCallback(async (contactCode: string) => {
    try {
      const entry = await fetchSingleContact(contactCode);
      setSquarePaymentCache((prev) => ({ ...prev, [contactCode]: entry }));
    } catch (err) {
      console.error('[useSquarePaymentData] Refresh failed:', contactCode, err);
    }
  }, []);

  /**
   * Pusher payment.updated イベントの購読
   */
  useEffect(() => {
    const unsubscribe = pusherService.subscribe((eventName: string, data: any) => {
      if (eventName === 'payment.updated' && data?.contact_code) {
        console.log('[useSquarePaymentData] Pusher payment.updated:', data.contact_code);
        refreshSingleContact(data.contact_code);
      }
    });
    return unsubscribe;
  }, [refreshSingleContact]);

  return {
    squarePaymentCache,
    squarePaymentLoading,
    fetchPaymentData,
    getPaymentData,
    setSquarePaymentCache,
    refreshSingleContact,
  };
}
